import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { euro, toCents, dt, POOL_STATE_LABELS } from "@/lib/format";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, Wallet, Boxes, Building2, Plus, Check, AlertTriangle, Store, Home, PackageCheck } from "lucide-react";
import { toast } from "sonner";

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [dep, setDep] = useState(null);
  const [depForm, setDepForm] = useState({ amount: "", type: "topup", description: "" });
  const [depOpen, setDepOpen] = useState(false);
  const [stForm, setStForm] = useState({ name: "", address: "", rooms: 0, beds: 0, sleeps: 0, notes: "" });
  const [stOpen, setStOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/customers/${id}`).then((r) => setD(r.data));
    api.get(`/customers/${id}/deposit`).then((r) => setDep(r.data));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <Loading />;
  const c = d.customer;

  const addDeposit = async () => {
    try {
      await api.post(`/customers/${id}/deposit`, { amount_cents: toCents(depForm.amount), type: depForm.type, description: depForm.description });
      toast.success("Movimento cauzione registrato"); setDepOpen(false); setDepForm({ amount: "", type: "topup", description: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const addStructure = async () => {
    try {
      await api.post(`/customers/${id}/structures`, { ...stForm, rooms: +stForm.rooms, beds: +stForm.beds, sleeps: +stForm.sleeps });
      toast.success("Struttura aggiunta"); setStOpen(false); setStForm({ name: "", address: "", rooms: 0, beds: 0, sleeps: 0, notes: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <button onClick={() => nav("/clienti")} className="flex items-center gap-2 text-sm text-slate-500 mb-4 hover:text-[#0B2447]"><ArrowLeft size={16} />Torna ai clienti</button>
      <PageHeader title={c.display_name} subtitle={`${c.code} · ${c.type.toUpperCase()} · ${c.phone || "—"} · ${c.email || "—"}`}>
        <StatusBadge status={c.status} />
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <p className="text-xs uppercase font-semibold text-slate-400 flex items-center gap-2"><Wallet size={14} />Cauzione</p>
          <div className="grid grid-cols-3 gap-2 mt-3 text-center">
            <div><p className="text-[11px] text-slate-400">Versata</p><p className="stat-num text-lg text-[#0B2447]">{euro(dep?.summary.paid_cents)}</p></div>
            <div><p className="text-[11px] text-slate-400">Trattenuta</p><p className="stat-num text-lg text-orange-600">{euro(dep?.summary.withheld_cents)}</p></div>
            <div><p className="text-[11px] text-slate-400">Disponibile</p><p className="stat-num text-lg text-green-600">{euro(dep?.summary.available_cents)}</p></div>
          </div>
          <Dialog open={depOpen} onOpenChange={setDepOpen}>
            <DialogTrigger asChild><Button variant="outline" size="sm" className="w-full mt-4" data-testid="add-deposit-btn"><Plus size={14} className="mr-1" />Movimento cauzione</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Movimento cauzione</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Tipo</Label>
                  <Select value={depForm.type} onValueChange={(v) => setDepForm({ ...depForm, type: v })}>
                    <SelectTrigger className="mt-1" data-testid="deposit-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="initial">Deposito iniziale</SelectItem>
                      <SelectItem value="increase">Aumento dotazione</SelectItem>
                      <SelectItem value="topup">Reintegro</SelectItem>
                      <SelectItem value="refund">Restituzione cauzione</SelectItem>
                      <SelectItem value="release">Chiusura / rilascio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Importo (€)</Label><Input data-testid="deposit-amount" className="mt-1" value={depForm.amount} onChange={(e) => setDepForm({ ...depForm, amount: e.target.value })} placeholder="es. 200,00" /></div>
                <div><Label>Descrizione</Label><Input className="mt-1" value={depForm.description} onChange={(e) => setDepForm({ ...depForm, description: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={addDeposit} data-testid="save-deposit" className="bg-[#0B2447]">Registra</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </Card>

        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase font-semibold text-slate-400 flex items-center gap-2"><Boxes size={14} />Dotazione affidata · Valore {euro(d.allocation.total_value_cents)}</p>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${d.deposit_covers_allocation ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>
              {d.deposit_covers_allocation ? <Check size={12} /> : <AlertTriangle size={12} />}{d.deposit_covers_allocation ? "Coperta" : "Scoperta"}
            </span>
          </div>
          {d.allocation.items.length === 0 ? <p className="text-sm text-slate-400 mt-4">Nessuna biancheria attualmente affidata.</p> : (
            <div className="mt-3 space-y-1.5">
              {d.allocation.items.map((a) => (
                <div key={a.article_id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                  <span className="text-slate-600">{a.article_name}</span>
                  <span className="font-semibold text-[#0B2447]">{a.qty} pz · {euro(a.value_cents)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {(d.open_anomalies.length > 0 || d.pending_returns.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {d.open_anomalies.length > 0 && (
            <Card className="p-4 border-orange-200"><p className="font-semibold text-orange-700 flex items-center gap-2 mb-2"><AlertTriangle size={16} />Anomalie aperte</p>
              {d.open_anomalies.map((a) => <p key={a.id} className="text-sm text-slate-600">{a.article_name} · {a.qty} pz · {euro(a.value_cents)}</p>)}
            </Card>
          )}
          {d.pending_returns.length > 0 && (
            <Card className="p-4 border-cyan-200"><p className="font-semibold text-[#0EA5C4] flex items-center gap-2 mb-2"><PackageCheck size={16} />Restituzioni da verificare</p>
              {d.pending_returns.map((r) => <p key={r.id} className="text-sm text-slate-600">{r.code} · {dt(r.created_at)}</p>)}
            </Card>
          )}
        </div>
      )}

      {c.type === "host" && (
        <Card className="p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="font-display font-bold text-[#0B2447] flex items-center gap-2"><Building2 size={18} />Strutture</p>
            <Dialog open={stOpen} onOpenChange={setStOpen}>
              <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="add-structure-btn"><Plus size={14} className="mr-1" />Aggiungi</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuova struttura</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Nome</Label><Input data-testid="structure-name" className="mt-1" value={stForm.name} onChange={(e) => setStForm({ ...stForm, name: e.target.value })} /></div>
                  <div><Label>Indirizzo</Label><Input className="mt-1" value={stForm.address} onChange={(e) => setStForm({ ...stForm, address: e.target.value })} /></div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Camere</Label><Input type="number" className="mt-1" value={stForm.rooms} onChange={(e) => setStForm({ ...stForm, rooms: e.target.value })} /></div>
                    <div><Label>Letti</Label><Input type="number" className="mt-1" value={stForm.beds} onChange={(e) => setStForm({ ...stForm, beds: e.target.value })} /></div>
                    <div><Label>Posti letto</Label><Input type="number" className="mt-1" value={stForm.sleeps} onChange={(e) => setStForm({ ...stForm, sleeps: e.target.value })} /></div>
                  </div>
                </div>
                <DialogFooter><Button onClick={addStructure} data-testid="save-structure" className="bg-[#0B2447]">Salva</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {d.structures.length === 0 ? <p className="text-sm text-slate-400">Nessuna struttura.</p> : (
            <div className="grid sm:grid-cols-2 gap-3">
              {d.structures.map((s) => (
                <div key={s.id} className="p-3 rounded-xl bg-slate-50">
                  <p className="font-semibold text-[#0B2447]">{s.name}</p>
                  <p className="text-xs text-slate-400">{s.address}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.rooms} camere · {s.beds} letti · {s.sleeps} posti letto</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <Card className="p-5">
        <p className="font-display font-bold text-[#0B2447] mb-3">Movimenti cauzione</p>
        {!dep || dep.movements.length === 0 ? <EmptyState title="Nessun movimento" /> : (
          <div className="divide-y">
            {dep.movements.map((m) => (
              <div key={m.id} className="flex items-center justify-between py-2.5 text-sm">
                <div><p className="text-slate-600">{m.description || m.type}</p><p className="text-xs text-slate-400">{dt(m.created_at)}</p></div>
                <span className={`font-bold ${m.amount_cents >= 0 ? "text-green-600" : "text-red-600"}`}>{m.amount_cents >= 0 ? "+" : ""}{euro(m.amount_cents)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
